<template>
  <div>
    <div v-if="project">
      <div class="is-flex is-justify-content-space-between is-align-items-center">
        <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
          <b-breadcrumb-item tag="a" @click.prevent="goToList()">
            {{ $t('projects.title') }}
          </b-breadcrumb-item>
          <b-breadcrumb-item tag="span" active>
            <InlineNameEditor
              :model-value="project?.name || id"
              :can-edit="!!project"
              :placeholder="$t('projects.name')"
              :save="saveProjectName"
            />
          </b-breadcrumb-item>
        </b-breadcrumb>
        <div class="field is-grouped">
          <div class="control">
              <b-button type="is-primary" icon-left="content-save" :disabled="!anyDirty" :title="$t('projects.saveTooltip')" @click="saveAll">
                {{ $t('projects.save') }}
              </b-button>
          </div>
          <div class="control">
              <b-button type="is-info" icon-left="hammer" :loading="building" :title="$t('projects.buildTooltip')" @click="buildAll">
                {{ $t('projects.build') }}
              </b-button>
          </div>
        </div>
      </div>

      <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
        <div style="flex: 1 1 auto; min-width: 0;">
          <b-tabs v-model="currentPath" >
            <b-tab-item :value="'__new__'">
              <template #header>
                  <b-icon icon="file-plus-outline"></b-icon>
                  <span>{{ $t('projects.addFile') }}</span>
              </template>
            </b-tab-item>
            <b-tab-item
              v-for="f in filteredFiles"
              :key="f.path"
              :value="f.path"
              :lazy="true"
            >
              <template #header>
                <b-icon :icon="fileIcon(f.path)"></b-icon>
                <span :class="{ 'has-text-weight-bold': isDirty(f.path) }">{{ f.name }}</span>
                <b-icon icon="close" size="is-small" @click.stop="confirmRemoveFile(f.path)"></b-icon>
              </template>
              <div class="box">
                <div class="codemirror-wrapper">
                  <codemirror
                    :model-value="fileStates[f.path]?.content ?? ''"
                    @update:modelValue="onContentChange(f.path, $event)"
                    :style="{ height: '75vh', width: '100%' }"
                    :indent-with-tab="true"
                    :tab-size="2"
                    :extensions="cmExtensionsFor(f.path)"
                    @ready="onEditorReady(f.path, $event)"
                  />
                </div>
              </div>
            </b-tab-item>
          </b-tabs>
        </div>
      </div>
      <b-message v-if="buildResult" :type="buildResult?.success ? 'is-success' : 'is-danger'" class="mt-2">
        <template v-if="buildResult?.success">
          <div class="is-flex is-justify-content-space-between is-align-items-center">
            <div>
              {{ $t('projects.buildSucceeded') }} —
              <template v-if="firmwareSha">
                <router-link :to="{ name: 'firmware-details', params: { sha: firmwareSha } }">firmware.bin</router-link> 
                ({{ formatSize(firmwareArtifact.size) }})
              </template>
              <template v-else>
                firmware.bin ({{ formatSize(firmwareArtifact.size) }})
              </template>
            </div>
            <div v-if="firmwareArtifact" class="field is-grouped">
              <b-select v-model="selectedDeviceId" :placeholder="$t('projects.selectDevice')">
                <option v-for="d in deviceList" :key="d.id" :value="d.id">{{ d.name || d.id }}</option>
              </b-select>
              <div class="control">
                <b-button type="is-danger" icon-left="fire" :loading="building" :title="$t('projects.flashTooltip')" @click="flashFirmware">
                  {{ $t('projects.flash') }}
                </b-button>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          {{ $t('projects.buildFailed') }}
        </template>
      </b-message>
      <div v-if="buildLogs !== null" class="box build-logs">
        <div class="logs-inner">
          <div v-for="(line, idx) in buildLogLines" :key="idx" class="is-family-monospace is-size-7"
              style="white-space: pre;"
              @click="onLogLineClick(line, $event)"
              v-html="buildLogLinesHtml[idx]">
          </div>
          <div v-if="building && !buildResult" class="typing-dots" aria-live="polite" :aria-label="$t('projects.building')">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="has-text-centered">
        <p class="is-size-4">{{ $t('projects.notFound') }}</p>
        <b-button @click="goToList()" class="mt-4">
          {{ $t('global.back') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref, watch, inject, onBeforeUnmount, getCurrentInstance, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { Codemirror } from 'vue-codemirror'
import InlineNameEditor from '@/components/InlineNameEditor.vue'
import { javascript } from '@codemirror/lang-javascript'
import { cpp } from '@codemirror/lang-cpp'
import { oneDark } from '@codemirror/theme-one-dark'
import { vsCodeLight } from '@fsegurai/codemirror-theme-vscode-light'
import { AnsiUp } from 'ansi_up'
import { formatSize } from '@/utils/formatters'
import type { EditorView } from '@codemirror/view'
import KeyCloakService from '@/security/KeycloakService'
import { models } from '@/store'

export default defineComponent({
  name: 'ProjectEdit',
  components: { codemirror: Codemirror, InlineNameEditor },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const isDarkMode = inject('isDarkMode') as { value: boolean } | undefined
    const id = computed(() => String(route.params.id || ''))
    const project = computed<any>(() => (store.state.projects || {})[id.value])
    const name = ref('')
    const saving = ref(false)
    const building = ref(false)
    const buildLogs = ref<string | null>(null)
    const buildLogLines = computed(() => (buildLogs.value || '').split(/\r?\n/))
    const buildResult = ref<{ success: boolean, firmwareSha: string, artifacts: Array<{ path: string, size: number }> } | null>(null)
    const flashing = ref(false)
    const selectedDeviceId = ref<string | number | null>(null)
    const stripBuildResultMarker = (s: string): string => {
      // Remove marker and any content after it
      return (s || '').replace(/\n?##BUILD_RESULT##[\s\S]*$/m, '')
    }
    const ansiUp = new AnsiUp()
    ansiUp.use_classes = true
    function linkifyAnsiHtml(html: string): string {
      const container = document.createElement('span')
      container.innerHTML = html
      const urlRegex = /\bhttps?:\/\/[^\s<>()'"`]+/gi
      const processNode = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue || ''
          urlRegex.lastIndex = 0
          const hasUrl = urlRegex.test(text)
          if (!hasUrl) return
          const frag = document.createDocumentFragment()
          let lastIndex = 0
          urlRegex.lastIndex = 0
          let match: RegExpExecArray | null
          while ((match = urlRegex.exec(text)) !== null) {
            const url = match[0].replace(/[),.;:]+$/, '')
            const before = text.slice(lastIndex, match.index)
            if (before) frag.appendChild(document.createTextNode(before))
            const a = document.createElement('a')
            a.href = url
            a.textContent = url
            a.target = '_blank'
            a.rel = 'noopener noreferrer'
            frag.appendChild(a)
            lastIndex = match.index + match[0].length
          }
          const tail = text.slice(lastIndex)
          if (tail) frag.appendChild(document.createTextNode(tail))
          if (node.parentNode) node.parentNode.replaceChild(frag, node)
          return
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element
          if (el.tagName === 'A') return
          const children = Array.from(node.childNodes)
          for (const child of children) processNode(child)
        }
      }
      const children = Array.from(container.childNodes)
      for (const child of children) processNode(child)
      return container.innerHTML
    }
    function linkifyFileRefs(html: string): string {
      const container = document.createElement('span')
      container.innerHTML = html
      const fileRefRegex = /(?:^|\s)([^\s:()]+\.(?:c|cpp|ino|h|hpp)):(\d+):?/i
      let matched: { file: string; line: number } | null = null
      const scanNode = (node: Node) => {
        if (matched) return
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue || ''
          const m = text.match(fileRefRegex)
          if (m) {
            const file = m[1]
            const lineNum = parseInt(m[2], 10)
            // find the file with with longest matching suffix to file
            const matchingFile = files.value.find(f => file.endsWith(f.path))
            if (matchingFile) {
              matched = { file: matchingFile.path, line: lineNum }
            }
          }
          return
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element
          if (el.tagName === 'A') return
          const children = Array.from(node.childNodes)
          for (const child of children) scanNode(child)
        }
      }
      const children = Array.from(container.childNodes)
      for (const child of children) scanNode(child)
      if (matched) {
        const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
        const wrapped = document.createElement('span')
        const a = document.createElement('a')
        a.href = '#'
        a.className = 'log-line-link'
        a.setAttribute('data-open-file', escapeAttr(matched.file))
        a.setAttribute('data-open-line', String(matched.line))
        a.innerHTML = container.innerHTML
        wrapped.appendChild(a)
        return wrapped.innerHTML
      }
      return container.innerHTML
    }
    const buildLogLinesHtml = computed(() => buildLogLines.value.map(l => {
      const html = ansiUp.ansi_to_html(l)
      const withUrls = linkifyAnsiHtml(html)
      return linkifyFileRefs(withUrls)
    }))

    const deviceList = computed<Array<{ id: string | number, name?: string }>>(() => {
      const devices = (store.state as any).devices || {}
      return Object.values(devices)
    })

    const firmwareArtifact = computed<{ path: string, size: number } | null>(() => {
      const arts = buildResult.value?.artifacts || []
      const match = arts.find(a => a && typeof a.path === 'string' && /(^|\/)firmware-merged\.bin$/i.test(a.path))
      return match || null
    })

    const firmwareSha = computed(() => buildResult.value?.firmwareSha || null)

    // use shared formatter from utils

    const files = ref<Array<{ name: string; path: string }>>([])
    const currentPath = ref('')
    const fileStates = ref<Record<string, { content: string; original: string; loaded: boolean }>>({})
    const editorViews = ref<Record<string, EditorView | undefined>>({})
    const anyDirty = computed(() => filteredFiles.value.some(f => (fileStates.value[f.path]?.content ?? '') !== (fileStates.value[f.path]?.original ?? '')))
    const vm = getCurrentInstance()

    const load = async () => {
      await store.dispatch('loadModel', { model: 'projects' })
      name.value = project.value?.name || ''
    }

    const isCodeFile = (p: string) => /\.(cpp|ino|h)$/i.test(p) || p === 'platformio.ini'
    const filteredFiles = computed(() => files.value.filter(f => isCodeFile(f.name)))
    const loadFiles = async (force = false) => {
      if (!id.value) return
      if (!force && files.value.length > 0) return
      const list = await store.dispatch('listProjectFiles', { id: id.value })
      // expect list as array of { name, path }
      files.value = Array.isArray(list) ? list : []
    }

    const detectLanguage = (p: string) => {
      const ext = (p.split('.').pop() || '').toLowerCase()
      if (ext === 'cpp' || ext === 'h' || ext === 'ino') return 'cpp'
      return 'text'
    }

    const fileIcon = (p: string): string => {
    //   const ext = (p.split('.').pop() || '').toLowerCase()
    //   if (ext === 'cpp') return 'language-c'
    //   if (ext === 'h' || ext === 'hpp') return 'file-outline'
    //   if (ext === 'ino') return 'chip'
      return 'file-code-outline'
    }

    const cmExtensionsFor = (path: string) => {
      const lang = detectLanguage(path)
      const base = lang === 'cpp' ? [cpp()] : [javascript()]
      const theme = (isDarkMode && isDarkMode.value) ? oneDark : vsCodeLight
      return [...base, theme]
    }

    const ensureFileState = (path: string) => {
      if (!fileStates.value[path]) {
        fileStates.value[path] = { content: '', original: '', loaded: false }
      }
      return fileStates.value[path]
    }

    const loadFileIfNeeded = async (path: string) => {
      if (!id.value || !path) return
      const st = ensureFileState(path)
      if (st.loaded) return
      const txt = await store.dispatch('readProjectFile', { id: id.value, path })
      st.content = txt
      st.original = txt
      st.loaded = true
    }

    const isDirty = (path: string) => {
      const st = fileStates.value[path]
      if (!st) return false
      return st.content !== st.original
    }

    const onEditorReady = (path: string, payload: any) => {
      const view: EditorView | undefined = (payload && payload.view) || payload
      if (view) editorViews.value[path] = view
    }

    const saveAll = async () => {
      if (!id.value) return
      const toSave = filteredFiles.value
        .map(f => f.path)
        .filter(p => isDirty(p))
      if (toSave.length === 0) return
      await Promise.all(toSave.map(async p => {
        const st = fileStates.value[p]
        await store.dispatch('writeProjectFile', { id: id.value, path: p, content: st.content })
        st.original = st.content
      }))
    }

    let buildAbort: AbortController | null = null
    const buildAll = async () => {
      if (!id.value || building.value) return
      building.value = true
      buildLogs.value = ''
      buildResult.value = null
      try {
        // Save all dirty files first
        await saveAll()
        // Prefer streaming endpoint; fallback to non-streaming JSON (no logs)
        buildAbort?.abort()
        buildAbort = new AbortController()
        const signal = buildAbort.signal
        const base = (models && (models as any).projects) || '/api/v1/projects'
        const url = `${base}/${encodeURIComponent(id.value)}/build`
        const resp = await fetch(url, {
          method: 'GET',
          headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` },
          signal,
        })
        if (!resp.ok) {
          const text = await resp.text().catch(() => `HTTP error! Status: ${resp.status}`)
          throw new Error(text)
        }
        if (!resp.body) {
          // No streaming body; read entire text and display and try to parse result marker
          const text = await resp.text()
          buildLogs.value = stripBuildResultMarker(text)
          const m = text.match(/##BUILD_RESULT##\s+(\{[\s\S]*?\})\n/) // greedy-safe JSON capture
          if (m) {
            try { buildResult.value = JSON.parse(m[1]) } catch (_) { /* ignore */ }
          }
          return
        }
        const reader = resp.body.getReader()
        const decoder = new TextDecoder()
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          const chunk = decoder.decode(value, { stream: true })
          buildLogs.value += chunk
        }
        // Try to parse BuildResult from the accumulated logs
        const m = (buildLogs.value || '').match(/##BUILD_RESULT##\s+(\{[\s\S]*?\})\n/)
        if (m) {
          try { buildResult.value = JSON.parse(m[1]) } catch (_) { /* ignore */ }
        }
        // Strip the marker from shown logs
        buildLogs.value = stripBuildResultMarker(buildLogs.value)
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          buildLogs.value = String(e?.message || e)
        }
      } finally {
        building.value = false
      }
    }

    const flashFirmware = async () => {
      if (!id.value || !firmwareArtifact.value || !selectedDeviceId.value) return
      try {
        flashing.value = true
        // Download artifact bytes from project files endpoint
        const base = (models && (models as any).projects) || '/api/v1/projects'
        const url = `${base}/${encodeURIComponent(id.value)}/files/${encodeURIComponent(firmwareArtifact.value.path)}`
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` } })
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const buf = await resp.arrayBuffer()
        const blob = new Blob([buf], { type: 'application/octet-stream' })
        const form = new FormData()
        form.append('file', blob, 'firmware.bin')
        const putUrl = `${(models as any).devices}/${encodeURIComponent(String(selectedDeviceId.value))}/firmware`
        const putResp = await fetch(putUrl, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` },
          body: form,
        })
        if (!putResp.ok) {
          const txt = await putResp.text().catch(() => '')
          throw new Error(txt || `HTTP ${putResp.status}`)
        }
        const proxy = vm?.proxy as any
        proxy?.$buefy?.toast?.open?.({ message: proxy?.$t('projects.otaTriggered'), type: 'is-success' })
      } catch (e: any) {
        const proxy = vm?.proxy as any
        proxy?.$buefy?.toast?.open?.({ message: `${proxy?.$t('projects.flashFailed')}: ${String(e?.message || e)}`, type: 'is-danger' })
      } finally {
        flashing.value = false
      }
    }

    const flashAll = async () => {
      if (!selectedDeviceId.value) {
        const proxy = vm?.proxy as any
        proxy?.$buefy?.toast?.open?.({ message: proxy?.$t('projects.selectDeviceFirst'), type: 'is-warning' })
        return
      }
      // Build first, then flash if firmware.bin is available
      await buildAll()
      if (buildResult.value?.success && firmwareArtifact.value) {
        await flashFirmware()
      }
    }

    const onLogLineClick = async (line: string, evt?: MouseEvent) => {
      if (evt) {
        const target = evt.target as HTMLElement | null
        const anchor = target && target.closest ? target.closest('a[data-open-file]') as HTMLElement | null : null
        if (anchor) {
          evt.preventDefault()
          const rel = anchor.getAttribute('data-open-file') || ''
          const lineStr = anchor.getAttribute('data-open-line') || '1'
          const lineNum = parseInt(lineStr, 10)
          if (files.value.some(f => f.path === rel)) {
            currentPath.value = rel
            await loadFileIfNeeded(rel)
            await nextTick()
            const view = editorViews.value[rel]
            if (view && Number.isFinite(lineNum)) {
              const total = view.state.doc.lines
              const ln = Math.max(1, Math.min(lineNum, total))
              const pos = view.state.doc.line(ln).from
              view.dispatch({ selection: { anchor: pos }, scrollIntoView: true })
              view.focus()
            }
          }
          return
        }
      }
      // Try to detect file:line:col patterns and open that file/tab
      // Common formats: "src/main.cpp:123:"
      // Ignore "ansi" sequences
      const ansiRegex = new RegExp(`[${String.fromCharCode(27)}${String.fromCharCode(155)}][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]`, 'g')
      line = line.replace(ansiRegex, '')
      // Try to detect file:line:col patterns and open that file/tab
      const m = line.match(/(?:^|\s)([^\s:()]+\.(?:c|cpp|ino|h|hpp))(?:[:(])(\d+)(?:[:)])?/i)
      if (m) {
        const rel = m[1]
        const lineNum = parseInt(m[2], 10)
        // Switch to the file tab and ensure it is loaded
        if (files.value.some(f => f.path === rel)) {
          currentPath.value = rel
          await loadFileIfNeeded(rel)
          await nextTick()
          const view = editorViews.value[rel]
          if (view && Number.isFinite(lineNum)) {
            const total = view.state.doc.lines
            const ln = Math.max(1, Math.min(lineNum, total))
            const pos = view.state.doc.line(ln).from
            view.dispatch({ selection: { anchor: pos }, scrollIntoView: true })
            view.focus()
          }
        }
        return
      }
    }

    const removeFile = async (path: string) => {
      try {
        await store.dispatch('deleteProjectFile', { id: id.value, path })
          // refresh file list and reset if needed
          await loadFiles(true)
          if (fileStates.value[path]) {
            delete fileStates.value[path]
          }
          if (currentPath.value === path) {
            currentPath.value = ''
            if (filteredFiles.value.length > 0) {
              currentPath.value = filteredFiles.value[0].path
            }
          }
      } catch (e) {
        // ignore
      }
    }

    const confirmRemoveFile = (path: string) => {
      type DialogProxy = { $buefy?: { dialog?: { confirm?: (opts: { message: string; type?: string; confirmText?: string; cancelText?: string; hasIcon?: boolean; onConfirm?: () => void }) => void } } }
      type I18nProxy = { $t?: (key: string, vars?: Record<string, string>) => string }
      const proxy = vm?.proxy as unknown as DialogProxy & I18nProxy
      const msg = proxy.$t('projects.confirmDeleteFile', { name: path })
      const confirmText = proxy?.$t ? proxy.$t('global.delete') : 'Delete'
      const cancelText = proxy?.$t ? proxy.$t('global.cancel') : 'Cancel'
      proxy?.$buefy?.dialog?.confirm?.({
        message: msg,
        type: 'is-danger',
        confirmText,
        cancelText,
        hasIcon: true,
        onConfirm: () => { removeFile(path) }
      })
    }

    const addNewFile = async () => {
      if (!id.value) return
      const proxy = vm?.proxy as any
      const promptForName = () => new Promise<string | null>((resolve) => {
        proxy?.$buefy?.dialog?.prompt?.({
          message: proxy?.$t('projects.enterNewFileName'),
          inputAttrs: { value: 'main.ino', placeholder: 'src/main.ino' },
          trapFocus: true,
          onConfirm: (value: string) => resolve((value ?? '').toString()),
          onCancel: () => resolve(null)
        })
      })
      let nameInput = await promptForName()
      if (nameInput === null) return
      nameInput = String(nameInput).trim()
      if (!nameInput) return
      if (!/\.(cpp|ino|h)$/i.test(nameInput)) nameInput = `${nameInput}.ino`
      // ensure files are created under src/
      const normalized = nameInput.replace(/^\/+/, '')
      const fullPath = normalized.startsWith('src/') ? normalized : `src/${normalized}`
      // prevent duplicates
      if (files.value.some(f => f.path === fullPath)) {
        proxy?.$buefy?.toast?.open?.({ message: proxy?.$t('projects.fileAlreadyExists'), type: 'is-warning' })
        return
      }
      const initialContent = ''
      try {
        await store.dispatch('writeProjectFile', { id: id.value, path: fullPath, content: initialContent })
        await loadFiles(true)
        // initialize state and switch to it; content is already saved so mark loaded
        fileStates.value[fullPath] = { content: initialContent, original: initialContent, loaded: true }
        currentPath.value = fullPath
      } catch (e) {
        // ignore
      }
    }


    const saveProjectName = async (newName: string) => {
      if (!id.value) return
      try {
        await store.dispatch('upsertProject', { id: id.value, body: { name: newName } })
      } catch (e) {
        // ignore, errors surfaced via API
      }
    }

    const goToList = () => router.push({name: 'project-list'})

    const onContentChange = (path: string, value: string) => {
      const st = ensureFileState(path)
      st.content = value
    }

    const onKey = (e: KeyboardEvent) => {
      const isSave = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's'
      if (isSave) {
        e.preventDefault()
        void saveAll()
      }
    }

    onMounted(async () => {
      await load()
      // ensure devices are loaded for flashing dropdown
      await store.dispatch('loadModel', { model: 'devices' })
      await loadFiles()
      if (filteredFiles.value.length > 0) {
        currentPath.value = filteredFiles.value[0].path
        await loadFileIfNeeded(currentPath.value)
      }
      window.addEventListener('keydown', onKey)
    })
    watch(() => currentPath.value, async (nv, ov) => {
      if (nv === '__new__') {
        await addNewFile()
        if (currentPath.value === '__new__') {
          currentPath.value = ov || ''
        }
      } else if (nv) {
        await loadFileIfNeeded(nv)
      }
    })
    watch(() => id.value, async () => {
      await load()
      files.value = []
      currentPath.value = ''
      fileStates.value = {}
      await loadFiles(true)
      if (filteredFiles.value.length > 0) {
        currentPath.value = filteredFiles.value[0].path
        await loadFileIfNeeded(currentPath.value)
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', onKey)
      if (buildAbort) {
        buildAbort.abort()
      }
    })

    return { id, project, name, saving, building, buildLogs, buildLogLines, buildLogLinesHtml, buildResult, goToList, files, filteredFiles, currentPath, fileStates, anyDirty, loadFiles, saveAll, buildAll, onLogLineClick, onEditorReady, cmExtensionsFor, fileIcon, confirmRemoveFile, isDirty, onContentChange, deviceList, firmwareArtifact, firmwareSha, formatSize, selectedDeviceId, flashFirmware, flashAll, flashing, saveProjectName }
  }
})
</script>

<style scoped>
.build-logs {
  overflow: auto;
  display: flex;
  flex-direction: row;
}
.build-logs .logs-inner {
  width: 0;
  flex-grow: 1;
}
.typing-dots {
  display: inline-block;
  padding: 4px 0 8px 0;
}
.typing-dots span {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin: 0 2px;
  background: currentColor;
  border-radius: 50%;
  animation: jump 1s infinite ease-in-out;
}
.typing-dots span:nth-child(2) { animation-delay: 0.15s; }
.typing-dots span:nth-child(3) { animation-delay: 0.3s; }
@keyframes jump {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.6; }
  40% { transform: translateY(-4px); opacity: 1; }
}
</style>
